import type { PublishEventOptions } from 'Lib/eventsSDK/publisher';
import { createAction, type PayloadCreator } from 'Lib/reduxUtils';
import { milliseconds } from 'Lib/utils/perfTimer';
import uuid from 'Lib/utils/uuid';

import { APP_EVENT_PLATFORM } from 'EventModels/app/appBuilder';
import type { OneOfAppPlatformEventValue } from 'EventModels/app/platformBuilder';
import { TRACK_EVENT, TRACK_SPARKS_EVENT } from 'Store/sparks/constants';
import type { AnalyticsEventActionType } from 'Store/sparks/reporter';
import type { TrackEventData } from 'Store/sparks/trackEventData';

import {
  DYNAMIC_UI_EDIT,
  type EventData as DynamicUIEditEventData,
} from './dynamicUI/events/dynamicUIEdit';
import {
  EDIT_PROFILE_INTERACT,
  type EventData as EditProfileInteractEventData,
} from './profile/events/editProfileInteract';

type EventType = keyof EventDataTypeMap;

type EventDataTypeMap = {
  [DYNAMIC_UI_EDIT]: DynamicUIEditEventData;
  [EDIT_PROFILE_INTERACT]: EditProfileInteractEventData;
  // TODO: Remove this once all events are typed
  [key: string]: { [key: string]: any };
};

export type CreateEventAction = typeof createEventAction;

export type TrackEvent = typeof trackEvent;

const createEventAction = <T extends EventType>(
  eventType: T,
  eventData?: EventDataTypeMap[T],
  options?: any
) => ({
  eventType,
  eventData,
  options,
});

export const trackEvent = <T extends EventType>(
  eventType: T,
  eventData?: EventDataTypeMap[T],
  options?: any
) =>
  createAction(
    TRACK_SPARKS_EVENT,
    createEventAction as PayloadCreator<
      Parameters<CreateEventAction>,
      ReturnType<CreateEventAction>
    >
  )(eventType, eventData, options);

const trackAppEvent = (
  eventData: TrackEventData,
  options: PublishEventOptions = {}
): AnalyticsEventActionType => ({
  type: TRACK_EVENT,
  payload: {
    eventData,
    options,
    perfNow: milliseconds(),
    id: uuid(),
  },
});

export const trackPlatformAppEvent = (
  eventData: OneOfAppPlatformEventValue,
  options?: PublishEventOptions
): AnalyticsEventActionType =>
  trackAppEvent(
    {
      eventType: APP_EVENT_PLATFORM,
      eventData,
    },
    options
  );
